import React, { useState, useEffect } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity } from "react-native";
import CustomModal from "../../common/general/CustomModal/CustomModal";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import { Plus, Trash } from "@phosphor-icons/react";

const EditTaskModal = ({ visible, onClose, onSave, task = null, loading = false }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "DELIVERABLE",
    priority: "MEDIUM",
    estimatedHours: "",
    dueDate: "",
    dependencies: [],
    completed: false,
  });

  const [newDependency, setNewDependency] = useState("");

  const taskTypes = [
    { value: "DELIVERABLE", label: "Deliverable" },
    { value: "MILESTONE", label: "Milestone" },
    { value: "RESEARCH", label: "Research" },
    { value: "PRESENTATION", label: "Presentation" },
    { value: "DOCUMENTATION", label: "Documentation" },
    { value: "TESTING", label: "Testing" },
  ];

  const priorities = [
    { value: "LOW", label: "Low" },
    { value: "MEDIUM", label: "Medium" },
    { value: "HIGH", label: "High" },
    { value: "CRITICAL", label: "Critical" },
  ];

  // Populate form when task changes
  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || "",
        description: task.description || "",
        type: task.type || "DELIVERABLE",
        priority: task.priority || "MEDIUM",
        estimatedHours: task.estimatedHours ? task.estimatedHours.toString() : "",
        dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split("T")[0] : "",
        dependencies: task.dependencies || [],
        completed: task.completed || false,
      });
    }
  }, [task]);

  const handleSave = () => {
    if (!formData.title.trim()) {
      return;
    }

    const taskData = {
      ...formData,
      estimatedHours: formData.estimatedHours ? parseInt(formData.estimatedHours) : null,
      dueDate: formData.dueDate || null,
    };

    onSave(taskData);
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      title: "",
      description: "",
      type: "DELIVERABLE",
      priority: "MEDIUM",
      estimatedHours: "",
      dueDate: "",
      dependencies: [],
      completed: false,
    });
    setNewDependency("");
    onClose();
  };

  const addDependency = () => {
    if (newDependency.trim() && !formData.dependencies.includes(newDependency.trim())) {
      setFormData({
        ...formData,
        dependencies: [...formData.dependencies, newDependency.trim()],
      });
      setNewDependency("");
    }
  };

  const removeDependency = (index) => {
    setFormData({
      ...formData,
      dependencies: formData.dependencies.filter((_, i) => i !== index),
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "CRITICAL":
        return "#F04438";
      case "HIGH":
        return "#F79009";
      case "MEDIUM":
        return "#3E68FE";
      case "LOW":
        return "#12B76A";
      default:
        return "#667085";
    }
  };

  return (
    <CustomModal visible={visible} onRequestClose={handleClose} showCloseButton={true} title="Edit Task">
      <View style={styles.container}>
        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          {/* Completion Status */}
          <View style={styles.section}>
            <TouchableOpacity
              style={styles.completionToggle}
              onPress={() => setFormData({ ...formData, completed: !formData.completed })}>
              <View style={[styles.checkbox, formData.completed && styles.checkedBox]}>
                {formData.completed && <CustomText style={styles.checkmark} text="✓" useTranslationText={false} />}
              </View>
              <CustomText
                style={[styles.completionText, formData.completed && styles.completedText]}
                text={formData.completed ? "Task Completed" : "Mark as Completed"}
                useTranslationText={false}
              />
            </TouchableOpacity>
          </View>

          {/* Title */}
          <View style={styles.section}>
            <CustomText style={styles.label} text="Task Title *" useTranslationText={false} />
            <CustomTextInput
              value={formData.title}
              onChangeText={(text) => setFormData({ ...formData, title: text })}
              placeholder="Enter task title"
              style={styles.input}
            />
          </View>

          {/* Description */}
          <View style={styles.section}>
            <CustomText style={styles.label} text="Description" useTranslationText={false} />
            <CustomTextInput
              value={formData.description}
              onChangeText={(text) => setFormData({ ...formData, description: text })}
              placeholder="Enter task description"
              multiline
              numberOfLines={4}
              style={[styles.input, styles.textArea]}
            />
          </View>

          {/* Type and Priority Row */}
          <View style={styles.row}>
            <View style={[styles.section, styles.halfWidth]}>
              <CustomText style={styles.label} text="Type" useTranslationText={false} />
              <View style={styles.pickerContainer}>
                {taskTypes.map((type) => (
                  <TouchableOpacity
                    key={type.value}
                    style={[styles.pickerOption, formData.type === type.value && styles.selectedPickerOption]}
                    onPress={() => setFormData({ ...formData, type: type.value })}>
                    <CustomText
                      style={[styles.pickerText, formData.type === type.value && styles.selectedPickerText]}
                      text={type.label}
                      useTranslationText={false}
                    />
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={[styles.section, styles.halfWidth]}>
              <CustomText style={styles.label} text="Priority" useTranslationText={false} />
              <View style={styles.pickerContainer}>
                {priorities.map((priority) => (
                  <TouchableOpacity
                    key={priority.value}
                    style={[
                      styles.pickerOption,
                      formData.priority === priority.value && styles.selectedPickerOption,
                      formData.priority === priority.value && {
                        backgroundColor: getPriorityColor(priority.value),
                      },
                    ]}
                    onPress={() => setFormData({ ...formData, priority: priority.value })}>
                    <CustomText
                      style={[styles.pickerText, formData.priority === priority.value && styles.selectedPickerText]}
                      text={priority.label}
                      useTranslationText={false}
                    />
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>

          {/* Estimated Hours and Due Date Row */}
          <View style={styles.row}>
            <View style={[styles.section, styles.halfWidth]}>
              <CustomText style={styles.label} text="Estimated Hours" useTranslationText={false} />
              <CustomTextInput
                value={formData.estimatedHours}
                onChangeText={(text) => setFormData({ ...formData, estimatedHours: text })}
                placeholder="e.g. 8"
                keyboardType="numeric"
                style={styles.input}
              />
            </View>

            <View style={[styles.section, styles.halfWidth]}>
              <CustomText style={styles.label} text="Due Date" useTranslationText={false} />
              <CustomTextInput
                value={formData.dueDate}
                onChangeText={(text) => setFormData({ ...formData, dueDate: text })}
                placeholder="YYYY-MM-DD"
                style={styles.input}
              />
            </View>
          </View>

          {/* Dependencies */}
          <View style={styles.section}>
            <CustomText style={styles.label} text="Dependencies" useTranslationText={false} />
            <View style={styles.dependencyInputRow}>
              <CustomTextInput
                value={newDependency}
                onChangeText={setNewDependency}
                placeholder="Enter dependency task title"
                style={[styles.input, styles.dependencyInput]}
              />
              <CustomButton
                text="Add"
                size="s"
                styleType="primary"
                onPress={addDependency}
                style={styles.addDependencyButton}
                icon={<Plus size={16} color="#ffffff" />}
              />
            </View>

            {formData.dependencies.length > 0 && (
              <View style={styles.dependenciesList}>
                {formData.dependencies.map((dep, index) => (
                  <View key={index} style={styles.dependencyItem}>
                    <CustomText style={styles.dependencyText} text={dep} useTranslationText={false} />
                    <TouchableOpacity onPress={() => removeDependency(index)} style={styles.removeDependencyButton}>
                      <Trash size={16} color="#F04438" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </View>
        </ScrollView>

        <View style={styles.footer}>
          <CustomButton
            text="Cancel"
            size="m"
            styleType="secondary"
            onPress={handleClose}
            style={styles.cancelButton}
          />
          <CustomButton
            text="Save Changes"
            size="m"
            styleType="primary"
            onPress={handleSave}
            loading={loading}
            disabled={!formData.title.trim() || loading}
            style={styles.saveButton}
          />
        </View>
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#ffffff",
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  section: {
    marginBottom: 24,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  halfWidth: {
    width: "48%",
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    color: "#101828",
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: "#101828",
    backgroundColor: "#ffffff",
  },
  textArea: {
    height: 100,
    textAlignVertical: "top",
  },
  completionToggle: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: "#EAECF0",
    backgroundColor: "#ffffff",
    marginRight: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  checkedBox: {
    backgroundColor: "#12B76A",
    borderColor: "#12B76A",
  },
  checkmark: {
    color: "#ffffff",
    fontSize: 12,
    fontWeight: "bold",
  },
  completionText: {
    fontSize: 16,
    color: "#101828",
    fontWeight: "500",
  },
  completedText: {
    color: "#12B76A",
  },
  pickerContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  pickerOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#EAECF0",
    backgroundColor: "#F9FAFB",
  },
  selectedPickerOption: {
    backgroundColor: "#3E68FE",
    borderColor: "#3E68FE",
  },
  pickerText: {
    fontSize: 14,
    color: "#667085",
    fontWeight: "500",
  },
  selectedPickerText: {
    color: "#ffffff",
  },
  dependencyInputRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  dependencyInput: {
    flex: 1,
    marginRight: 12,
  },
  addDependencyButton: {
    backgroundColor: "#3E68FE",
    minWidth: 80,
  },
  dependenciesList: {
    marginTop: 8,
  },
  dependencyItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: "#F9FAFB",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#EAECF0",
    marginBottom: 8,
  },
  dependencyText: {
    fontSize: 14,
    color: "#101828",
    flex: 1,
  },
  removeDependencyButton: {
    padding: 4,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
    backgroundColor: "#F9FAFB",
  },
  cancelButton: {
    flex: 1,
    marginRight: 12,
    backgroundColor: "#ffffff",
    borderColor: "#EAECF0",
    borderWidth: 1,
  },
  saveButton: {
    flex: 1,
    backgroundColor: "#3E68FE",
  },
});

export default EditTaskModal;
